import { Dialog } from "$components/dialog/dialog";
import { DialogBody } from "$components/dialog/dialog-body";
import { RadioGroupField } from "$components/rhf-fields";
import { styled, Typography } from "@material-ui/core";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { useIntl } from "react-intl";

const Warning = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
  gap: theme.spacing(1),
  padding: "12px",
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: "8px",
  marginBottom: theme.spacing(3),
}));

const StyledErrorOutlineIcon = styled(ErrorOutlineIcon)(({ theme }) => ({
  height: 24,
  width: 24,
  color: theme.palette.warning.main,
}));

const Text = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[700],
}));

const TextWithMarginTop = styled(Typography)(({ theme }) => ({
  marginTop: "12px",
  color: theme.palette.grey[700],
}));

export const GuardianConsentModal: FC<{
  open?: boolean;
  onClose: () => void;
  onSubmit: () => void;
  submitLoading: boolean;
  fieldNames: {
    healthcareComms: string;
    marketingComms?: string;
    elephantUseOfAnonData?: string;
  };
}> = ({ open = false, onClose, onSubmit, submitLoading, fieldNames }) => {
  const { formatMessage } = useIntl();

  return (
    <Dialog
      open={open}
      title={formatMessage({
        id: "guardianConsentModal.title",
        defaultMessage: "Data Consent",
      })}
      onSubmit={onSubmit}
      submitLabel={formatMessage({
        id: "guardianConsentModal.confirm",
        defaultMessage: "Accept & Continue",
      })}
      submitLoading={submitLoading}
      onCancel={onClose}
      cancelLabel={formatMessage({
        id: "guardianConsentModal.cancel",
        defaultMessage: "Cancel",
      })}
      data-testid="GuardianConsentDialog"
      style={{ overflowY: "scroll" }}
    >
      <DialogBody>
        <Warning>
          <StyledErrorOutlineIcon />
          <Typography variant="body1">
            {formatMessage({
              id: "guardianConsentModal.warning",
              defaultMessage: "Please show or read this text to the guardian",
            })}
          </Typography>
        </Warning>
        <div>
          <Text variant="body1" paragraph>
            {formatMessage({
              id: "guardianConsentModal.healthcareComms.title",
              defaultMessage:
                "Do you give permission for Elephant to contact you via SMS and whatsapp about this child’s healthcare?",
            })}
          </Text>
          <Text variant="body2" paragraph>
            {formatMessage({
              id: "guardianConsentModal.healthcareComms.description",
              defaultMessage:
                "This includes appointment reminders, treatment updates, and other medical related information and services.",
            })}
          </Text>
          <RadioGroupField
            resetMarginTop
            name={fieldNames.healthcareComms}
            required
            data-testid="consent.healthcareComms"
            options={[
              {
                label: "Yes",
                value: true,
                "data-testid": "consent.healthcareComms.yes",
              },
              {
                label: "No",
                value: false,
                "data-testid": "consent.healthcareComms.no",
              },
            ]}
          />
        </div>
        {fieldNames.marketingComms && (
          <div>
            <TextWithMarginTop variant="body1" paragraph>
              {formatMessage({
                id: "guardianConsentModal.marketingComms.title",
                defaultMessage:
                  "Do you give permission for Elephant to contact you about its products, news and events?",
              })}
            </TextWithMarginTop>
            <RadioGroupField
              resetMarginTop
              name={fieldNames.marketingComms}
              required
              data-testid="consent.marketingComms"
              options={[
                {
                  label: "Yes",
                  value: true,
                  "data-testid": "consent.marketingComms.yes",
                },
                {
                  label: "No",
                  value: false,
                  "data-testid": "consent.marketingComms.no",
                },
              ]}
            />
          </div>
        )}
        {fieldNames.elephantUseOfAnonData && (
          <div>
            <TextWithMarginTop variant="body1" paragraph>
              {formatMessage({
                id: "guardianConsentModal.elephantUseOfAnonData.title",
                defaultMessage:
                  "Do you give permission for Elephant to anonymise this child’s data so they can use it to improve healthcare?",
              })}
            </TextWithMarginTop>
            <RadioGroupField
              resetMarginTop
              name={fieldNames.elephantUseOfAnonData}
              required
              data-testid="consent.elephantUseOfAnonData"
              options={[
                {
                  label: "Yes",
                  value: true,
                  "data-testid": "consent.elephantUseOfAnonData.yes",
                },
                {
                  label: "No",
                  value: false,
                  "data-testid": "consent.elephantUseOfAnonData.no",
                },
              ]}
            />
          </div>
        )}
        <Text variant="body2">
          {formatMessage({
            id: "guardianConsentModal.changeInfoLabel",
            defaultMessage:
              "You can change your answers at any time by contacting Elephant through their website or we can message them for you. See our Privacy Policy for details.",
          })}
        </Text>
      </DialogBody>
    </Dialog>
  );
};
